import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Moon, Check, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { InquiryDialog } from "../components/InquiryDialog";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/packages`)
      .then((res) => setPackages(res.data))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, []);

  const inquire = (pkg) => {
    setSelected(pkg);
    setInquiryOpen(true);
  };

  return (
    <>
      <SEO
        title="Vacation Packages Store"
        description="Book curated, exclusive vacation packages — Maldives, Cancún, Santorini, Bora Bora, Bali and more. Pay in full or reserve with a deposit."
        path="/packages"
      />

      {/* HERO */}
      <section className="relative flex min-h-[56vh] items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1758717152007-6a2eb7299409?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Overwater bungalows over a turquoise lagoon at an exclusive resort"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 pt-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-white">
              <Sparkles className="h-4 w-4" /> Curated & Exclusive
            </span>
            <h1 className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Vacation Packages Store
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-200">
              Hand-picked escapes to the world's most exclusive destinations. Book online in
              minutes — pay in full or reserve your dates with a deposit.
            </p>
          </motion.div>
        </div>
      </section>

      {/* GRID */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>The Collection</Overline>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
            Choose your next escape.
          </h2>
        </Reveal>

        {loading ? (
          <div className="flex justify-center py-24" data-testid="packages-loading">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {packages.filter((p) => p && p.slug).map((pkg, i) => (
              <Reveal key={pkg.id || pkg.slug} delay={i * 0.06}>
                <article
                  data-testid={`package-card-${pkg.slug}`}
                  className="flex h-full flex-col overflow-hidden rounded-3xl card-3d"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={pkg.image}
                      alt={pkg.alt}
                      className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                    {pkg.tag && (
                      <span className="absolute right-4 top-4 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                        {pkg.tag}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-secondary">
                      <MapPin className="h-3.5 w-3.5" /> {pkg.destination}
                    </div>
                    <h3 className="mt-2 font-display text-xl font-bold text-navy">{pkg.name}</h3>

                    <ul className="mt-4 space-y-2">
                      {(pkg.highlights || []).map((h) => (
                        <li key={h} className="flex items-center gap-2 text-sm text-slate-600">
                          <Check className="h-4 w-4 flex-none text-secondary" />
                          {h}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 flex items-end justify-between border-t border-slate-100 pt-5">
                      <div>
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Moon className="h-3.5 w-3.5" /> {pkg.nights || 5} nights typical
                        </div>
                        <p className="mt-1 font-display text-2xl font-bold text-navy">
                          <span className="text-sm font-normal text-slate-400">from </span>
                          ${Number(pkg.price_from || 0).toLocaleString()}
                          <span className="text-sm font-normal text-slate-400"> /night</span>
                        </p>
                        <p className="text-xs text-slate-400">
                          or ${Number(pkg.deposit || 0).toLocaleString()} deposit to reserve
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-col gap-2">
                      <Button
                        asChild
                        data-testid={`package-book-${pkg.slug}`}
                        className="w-full rounded-full bg-primary py-6 text-base font-semibold text-primary-foreground btn-glow transition-transform duration-200 hover:scale-[1.02] hover:bg-primary/90"
                      >
                        <Link to={`/packages/${pkg.slug}`}>
                          View Details & Book <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </Button>
                      <button
                        data-testid={`package-inquire-${pkg.slug}`}
                        onClick={() => inquire(pkg)}
                        className="text-sm font-semibold text-primary hover:underline"
                      >
                        or ask a question first
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}

        {/* General inquiry banner */}
        <Reveal delay={0.05}>
          <div className="mt-16 flex flex-col items-center justify-between gap-6 rounded-[2rem] bg-navy p-10 text-center sm:flex-row sm:text-left">
            <div>
              <h3 className="font-display text-2xl font-bold text-white">
                Don't see your dream destination?
              </h3>
              <p className="mt-2 text-slate-300">
                Tell us where you want to go — we'll build a custom, transparent plan.
              </p>
            </div>
            <Button
              data-testid="general-inquiry-button"
              onClick={() => inquire(null)}
              className="rounded-full bg-primary px-8 py-6 text-base font-semibold text-primary-foreground btn-glow hover:bg-primary/90"
            >
              Start a Custom Inquiry
            </Button>
          </div>
        </Reveal>
      </section>

      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} pkg={selected} />
    </>
  );
}
